import React, { useEffect } from 'react';
import { FormData, HerbicideApplication, Product } from '../App';
import { Clock, Plus, Trash2, AlertCircle } from 'lucide-react';

interface Props {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
}

function HerbicideDetails({ formData, updateFormData }: Props) {
  useEffect(() => {
    // Initialize herbicides array if it's empty
    if (formData.herbicideApplications > 0 && formData.herbicides.length === 0) {
      const initialHerbicides: HerbicideApplication[] = Array(formData.herbicideApplications)
        .fill(null)
        .map(() => ({
          type: 'solo',
          products: [{ productName: '', cost: 0 }],
          sprayManCost: 0,
          controlDuration: 0,
        }));
      updateFormData({ herbicides: initialHerbicides });
    }
  }, [formData.herbicideApplications]);

  const handleApplicationTypeChange = (index: number, type: 'solo' | 'tank-mix') => {
    const newHerbicides = [...formData.herbicides];
    newHerbicides[index] = {
      type,
      products: [{ productName: '', cost: 0 }],
      sprayManCost: newHerbicides[index]?.sprayManCost || 0,
      controlDuration: newHerbicides[index]?.controlDuration || 0,
    };
    updateFormData({ herbicides: newHerbicides });
  };

  const handleProductChange = (applicationIndex: number, productIndex: number, field: keyof Product, value: string | number) => {
    const newHerbicides = [...formData.herbicides];
    const products = [...(newHerbicides[applicationIndex]?.products || [])];
    products[productIndex] = {
      ...products[productIndex],
      [field]: value,
    };
    newHerbicides[applicationIndex] = {
      ...newHerbicides[applicationIndex],
      products,
    };
    updateFormData({ herbicides: newHerbicides });
  };

  const handleApplicationChange = (index: number, field: keyof Omit<HerbicideApplication, 'type' | 'products'>, value: number) => {
    const newHerbicides = [...formData.herbicides];
    newHerbicides[index] = {
      ...newHerbicides[index],
      [field]: value,
    };
    updateFormData({ herbicides: newHerbicides });
  };

  const addProduct = (applicationIndex: number) => {
    const newHerbicides = [...formData.herbicides];
    newHerbicides[applicationIndex].products.push({ productName: '', cost: 0 });
    updateFormData({ herbicides: newHerbicides });
  };

  const removeProduct = (applicationIndex: number, productIndex: number) => {
    const newHerbicides = [...formData.herbicides];
    newHerbicides[applicationIndex].products = newHerbicides[applicationIndex].products.filter((_, index) => index !== productIndex);
    updateFormData({ herbicides: newHerbicides });
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Herbicide Details</h2>
        <p className="text-gray-600 mb-6">
          Enter the details for each herbicide application.
        </p>
      </div>

      {Array.from({ length: formData.herbicideApplications }).map((_, index) => {
        const application = formData.herbicides[index] || {
          type: 'solo',
          products: [{ productName: '', cost: 0 }],
          sprayManCost: 0,
          controlDuration: 0,
        };

        return (
          <div
            key={index}
            className="p-6 border border-gray-200 rounded-lg space-y-4 bg-gray-50"
          >
            <h3 className="text-lg font-semibold text-gray-800">
              Application #{index + 1}
            </h3>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Application Type
              </label>
              <select
                value={application.type}
                onChange={(e) => handleApplicationTypeChange(index, e.target.value as 'solo' | 'tank-mix')}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="solo">Solo</option>
                <option value="tank-mix">Tank Mix</option>
              </select>
            </div>

            <div className="space-y-4">
              {application.products.map((product, productIndex) => (
                <div key={productIndex} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Name
                    </label>
                    <input
                      type="text"
                      value={product.productName || ''}
                      onChange={(e) => handleProductChange(index, productIndex, 'productName', e.target.value)}
                      className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                        !product.productName ? 'border-red-300' : 'border-gray-300'
                      }`}
                      placeholder="Enter herbicide name"
                      required
                    />
                    {!product.productName && (
                      <p className="mt-1 text-sm text-red-500 flex items-center">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        Product name is required
                      </p>
                    )}
                  </div>

                  <div className="relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product Cost (₹/acre)
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <input
                          type="number"
                          min="1"
                          value={product.cost || ''}
                          onChange={(e) => handleProductChange(index, productIndex, 'cost', Number(e.target.value))}
                          className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                            product.cost <= 0 ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Enter cost per acre"
                          required
                        />
                        {product.cost <= 0 && (
                          <p className="mt-1 text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            Cost must be greater than 0
                          </p>
                        )}
                      </div>
                      {application.type === 'tank-mix' && application.products.length > 1 && (
                        <button
                          onClick={() => removeProduct(index, productIndex)}
                          className="p-2 text-red-600 hover:text-red-700 rounded-lg hover:bg-red-50"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {application.type === 'tank-mix' && (
                <button
                  onClick={() => addProduct(index)}
                  className="w-full p-2 border border-dashed border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Product
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spray Man Cost (₹/acre)
                </label>
                <input
                  type="number"
                  min="1"
                  value={application.sprayManCost || ''}
                  onChange={(e) => handleApplicationChange(index, 'sprayManCost', Number(e.target.value))}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    application.sprayManCost <= 0 ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter spray man cost per acre"
                  required
                />
                {application.sprayManCost <= 0 && (
                  <p className="mt-1 text-sm text-red-500 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Cost must be greater than 0
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Clock className="w-4 h-4 inline-block mr-1" />
                  Control Duration (days)
                </label>
                <input
                  type="number"
                  min="1"
                  value={application.controlDuration || ''}
                  onChange={(e) => handleApplicationChange(index, 'controlDuration', Number(e.target.value))}
                  className={`w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    application.controlDuration <= 0 ? 'border-red-300' : 'border-gray-300'
                  }`}
                  placeholder="Enter control duration"
                  required
                />
                {application.controlDuration <= 0 && (
                  <p className="mt-1 text-sm text-red-500 flex items-center">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Duration must be greater than 0
                  </p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default HerbicideDetails;